import axios from "axios";
import { LayoutGrid, Plus, Ruler, Tag, X } from "lucide-react";
import { useContext, useEffect, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import "../../../assets/css/admin/addProduct/AddProduct.css";
import Modal from "../../../components/admin/modal/ProductModel";
import AppContext from "../../../context/AppContext";

const MAX_IMAGES = 5;

/**
 * EditProduct
 *
 * Props:
 *  - isOpen          {boolean}   — controls modal visibility
 *  - onClose         {function}  — called when Cancel / close is clicked
 *  - product         {object}    — existing product data to pre-fill the form (same shape as ProductView's `product`)
 *  - onEdit          {function}  — optional, called with the updated product after a successful save
 *  - viewAllProducts {function}  — optional, called after a successful save to refresh a product list
 */
const EditProduct = ({
  isOpen,
  onClose,
  product = {},
  onEdit,
  viewAllProducts,
}) => {
  const fileInputRef = useRef(null);
  const { baseUrl, baseUrlForFile, authToken } = useContext(AppContext);

  // ── Basic product fields — pre-filled from `product` prop ──
  const [name, setName] = useState(product.name || "");
  const [price, setPrice] = useState(product.price || "");
  const [size, setSize] = useState(product.size || "");
  const [category, setCategory] = useState(product.adcategory_id || "");
  const [description, setDescription] = useState(product.description || "");
  const [keyFeatures, setKeyFeatures] = useState(product.keyFeatures || "");
  const [dimensions, setDimensions] = useState(product.dimensions || "");
  const [productMaterial, setProductMaterial] = useState(
    product.material || "",
  ); // will be shown to customer
  const [color, setColor] = useState(product.color || "");
  const [allCategories, setallCategories] = useState([]);
  const [allCategoriesForMaterial, setallCategoriesForMaterial] = useState([]);

  // Existing server images (id + image_path) vs newly picked local files
  const [existingImages, setExistingImages] = useState(
    product.productimages || [],
  );
  const [newImages, setNewImages] = useState([]);

  // ── PM (Product Materials) fields ──
  const [allMaterial, setAllMaterial] = useState([]);
  const [pmCategory, setPmCategory] = useState("");
  const [material, setMaterial] = useState("");
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");

  // Attached materials list — prefilled directly from product prop (already includes admaterial)
  const [attachedMaterials, setAttachedMaterials] = useState(
    product.productmaterials || [],
  );

  // ── Error and loading states ──
  const [errors, setErrors] = useState({});
  const [pmErrors, setPmErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const totalImages = existingImages.length + newImages.length;

  // ─────────────────────────────────────────────
  // Image helper functions

  const handleImageFiles = (files) => {
    const imageFiles = Array.from(files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (!imageFiles.length) return;
    const remaining = MAX_IMAGES - totalImages;
    const toAdd = imageFiles.slice(0, remaining);
    setNewImages((prev) => [...prev, ...toAdd]);
    if (errors.images) setErrors((p) => ({ ...p, images: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const removeNewImage = (idx) => {
    setNewImages((prev) => prev.filter((_, i) => i !== idx));
  };

  // ─────────────────────────────────────────────
  // function to fetch category in dropdown

  const viewCategories = (type) => {
    axios
      .get(`${baseUrl}/admin/category/viewCategories`, {
        headers: { Authorization: `Bearer${authToken}` },
        params: { type: type },
      })
      .then((response) => {
        console.log(response.data);
        if (type == "inventory") {
          setallCategoriesForMaterial(response.data.categories);
        } else {
          setallCategories(response.data.categories);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ─────────────────────────────────────────────
  // Product material helper functions

  // function to fetch materials against selected category
  const viewAllMaterials = () => {
    axios
      .get(`${baseUrl}/admin/inventory/viewInventory`, {
        headers: { Authorization: `Bearer${authToken}` },
        params: { adcategory_id: pmCategory },
      })
      .then((response) => {
        console.log(response.data);
        setAllMaterial(response.data.allMaterial);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // function to attach a material to this product
  const handleAttachMaterial = () => {
    if (!material) {
      setPmErrors({ material: "Please select a material" });
      return;
    }
    if (!quantity || Number(quantity) <= 0) {
      setPmErrors({ quantity: "Valid quantity is required" });
      return;
    }

    const formdata = new FormData();
    formdata.append("adproduct_id", product.id);
    formdata.append("admaterial_id", material);
    formdata.append("qty", quantity);

    axios
      .post(`${baseUrl}/admin/products/editProductMaterial`, formdata, {
        headers: { Authorization: `Bearer${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        const selected = allMaterial.find(
          (item) => String(item.id) === String(material),
        );
        setAttachedMaterials((prev) => [
          ...prev,
          {
            id: response.data.productMaterial?.id ?? `local-${Date.now()}`,
            qty: quantity,
            admaterial: {
              id: material,
              name: selected?.name || "",
              unit: selected?.unit || unit,
              adcategory: {
                id: pmCategory,
                name:
                  allCategoriesForMaterial.find(
                    (c) => String(c.id) === String(pmCategory),
                  )?.name || "",
              },
            },
          },
        ]);
      })
      .catch((error) => {
        console.log(error);
      });

    setMaterial("");
    setPmCategory("");
    setQuantity("");
    setUnit("");
    setPmErrors({});
  };

  // function to delete an attached material
  const handleDeleteMaterial = (id) => {
    setAttachedMaterials((prev) => prev.filter((m) => m.id !== id));

    const formdata = new FormData();
    formdata.append("id", id);
    axios
      .post(`${baseUrl}/admin/products/deleteProductMaterial`, formdata, {
        headers: { Authorization: `Bearer${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ─────────────────────────────────────────────
  // FORM VALIDATION
  // ─────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Product name is required";
    if (totalImages === 0)
      errs.images = "At least one product image is required";
    if (!size.trim()) errs.size = "Size is required";
    if (!price || Number(price) <= 0) errs.price = "Valid price is required";
    if (!category) errs.category = "Category is required";
    return errs;
  };

  // ─────────────────────────────────────────────
  // function to update product
  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSaving(true);

    const formdata = new FormData();
    formdata.append("id", product.id);
    formdata.append("name", name);
    newImages.forEach((file) => formdata.append("images[]", file));
    existingImages.forEach((img) =>
      formdata.append("keep_image_ids[]", img.id),
    );
    formdata.append("size", size);
    formdata.append("price", price);
    formdata.append("adcategory_id", category);
    formdata.append("description", description);
    formdata.append("keyFeatures", keyFeatures);
    formdata.append("dimensions", dimensions);
    formdata.append("material", productMaterial);
    formdata.append("color", color);

    axios
      .post(`${baseUrl}/admin/products/editProduct`, formdata, {
        headers: { Authorization: `Bearer${authToken}` },
      })
      .then((response) => {
        setIsSaving(false);
        setShowSuccess(true);
        onEdit?.(response.data.product);
        viewAllProducts?.();
        console.log(response.data.message);
      })
      .catch((error) => {
        setIsSaving(false);
        console.log(error);
      });
  };

  //function to close modal
  const handleClose = () => {
    if (!isSaving) {
      onClose?.();
    }
  };

  useEffect(() => {
    viewCategories("product");
    viewCategories("inventory");
  }, []);

  // To fetch materials against selected category
  useEffect(() => {
    if (pmCategory) {
      viewAllMaterials();
    }
  }, [pmCategory]);

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Product"
      size="large"
    >
      {showSuccess ? (
        <div className="ap-success">
          <div className="ap-success-icon">✓</div>
          <h3>Product Updated!</h3>
          <p>The product changes have been saved successfully.</p>
        </div>
      ) : (
        <form className="ap-form" onSubmit={handleSubmit} noValidate>
          {/* ════ BASIC INFO ════ */}
          <div className="ap-section">
            <div className="ap-grid">
              {/* Product Name */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-name">
                  Product Name *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon">
                    <Tag size={15} />
                  </span>
                  <input
                    className={`ap-input ${errors.name ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (errors.name) setErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder="e.g., Modern Velvet Sofa"
                  />
                </div>
                {errors.name && (
                  <span className="ap-error-text">{errors.name}</span>
                )}
              </div>

              {/* Images */}
              <div className="ap-field ap-field--full">
                <label className="ap-label">
                  Product Images *{" "}
                  <span className="ap-img-count">
                    {totalImages}/{MAX_IMAGES}
                  </span>
                </label>
                <div className="ap-img-grid">
                  {/* Existing server images */}
                  {existingImages.map((img) => (
                    <div key={img.id} className="ap-img-thumb">
                      <img
                        src={`${baseUrlForFile}${img.image_path}`}
                        alt="Product"
                      />
                      <button
                        type="button"
                        className="ap-img-thumb-remove"
                        onClick={() => removeExistingImage(img.id)}
                        aria-label="Remove image"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  {/* Newly added local images */}
                  {newImages.map((file, idx) => (
                    <div key={idx} className="ap-img-thumb">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`New ${idx + 1}`}
                      />
                      <button
                        type="button"
                        className="ap-img-thumb-remove"
                        onClick={() => removeNewImage(idx)}
                        aria-label="Remove image"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}

                  {totalImages < MAX_IMAGES && (
                    <button
                      type="button"
                      className="ap-img-add-slot"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Plus size={20} />
                      <span>Add</span>
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="ap-img-file-input"
                  onChange={(e) => handleImageFiles(e.target.files)}
                />
                {errors.images && (
                  <span className="ap-error-text">{errors.images}</span>
                )}
                <p className="ap-img-hint">
                  First image is shown as the main product photo.
                </p>
              </div>

              {/* Size */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-size">
                  Size *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon">
                    <Ruler size={15} />
                  </span>
                  <input
                    className={`ap-input ${errors.size ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-size"
                    value={size}
                    onChange={(e) => {
                      setSize(e.target.value);
                      if (errors.size) setErrors((p) => ({ ...p, size: "" }));
                    }}
                    placeholder="e.g., 3-Seater"
                  />
                </div>
                {errors.size && (
                  <span className="ap-error-text">{errors.size}</span>
                )}
              </div>

              {/* Description */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-description">
                  Description *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon"></span>
                  <input
                    className={`ap-input ${errors.description ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-description"
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (errors.description)
                        setErrors((p) => ({ ...p, description: "" }));
                    }}
                    placeholder="write the description for product."
                  />
                </div>
                {errors.description && (
                  <span className="ap-error-text">{errors.description}</span>
                )}
              </div>

              {/* Key Features */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-keyFeatures">
                  Key Features *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon"></span>
                  <input
                    className={`ap-input ${errors.keyFeatures ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-keyFeatures"
                    value={keyFeatures}
                    onChange={(e) => {
                      setKeyFeatures(e.target.value);
                      if (errors.keyFeatures)
                        setErrors((p) => ({ ...p, keyFeatures: "" }));
                    }}
                    placeholder="e.g., Water Resistant, Scratch Proof, Easy to Clean"
                  />
                </div>
                <p className="ap-img-hint">
                  Separate each feature with a comma.
                </p>
                {errors.keyFeatures && (
                  <span className="ap-error-text">{errors.keyFeatures}</span>
                )}
              </div>

              {/* Dimensions */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-dimensions">
                  Dimensions *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon"></span>
                  <input
                    className={`ap-input ${errors.dimensions ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-dimensions"
                    value={dimensions}
                    onChange={(e) => {
                      setDimensions(e.target.value);
                      if (errors.dimensions)
                        setErrors((p) => ({ ...p, dimensions: "" }));
                    }}
                    placeholder="e.g., 210cm x 90cm x 85cm"
                  />
                </div>
                {errors.dimensions && (
                  <span className="ap-error-text">{errors.dimensions}</span>
                )}
              </div>

              {/* Product Material -(material k names jo customer ko show hony hain) */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-productMaterial">
                  Product Material Name *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon"></span>
                  <input
                    className={`ap-input ${errors.productMaterial ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-productMaterial"
                    value={productMaterial}
                    onChange={(e) => {
                      setProductMaterial(e.target.value);
                      if (errors.productMaterial)
                        setErrors((p) => ({ ...p, productMaterial: "" }));
                    }}
                    placeholder="e.g., Velvet Fabric, Sheeham Wood"
                  />
                </div>
                {errors.productMaterial && (
                  <span className="ap-error-text">
                    {errors.productMaterial}
                  </span>
                )}
              </div>

              {/* color */}
              <div className="ap-field ap-field--full">
                <label className="ap-label" htmlFor="edit-prod-color">
                  color *
                </label>
                <div className="ap-input-wrap">
                  <span className="ap-input-icon"></span>
                  <input
                    className={`ap-input ${errors.color ? "ap-input--error" : ""}`}
                    type="text"
                    id="edit-prod-color"
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      if (errors.color) setErrors((p) => ({ ...p, color: "" }));
                    }}
                    placeholder="e.g., Emerald Green"
                  />
                </div>
                {errors.color && (
                  <span className="ap-error-text">{errors.color}</span>
                )}
              </div>
            </div>
          </div>

          {/* ════ PRICING ════ */}
          <div className="ap-section">
            <p className="ap-section-label">Pricing</p>
            <div className="ap-field ap-field--full">
              <label className="ap-label" htmlFor="edit-prod-price">
                Price *
              </label>
              <div className="ap-prefix-wrap">
                <span className="ap-prefix">PKR</span>
                <input
                  className={`ap-input ${errors.price ? "ap-input--error" : ""}`}
                  type="number"
                  id="edit-prod-price"
                  value={price}
                  onChange={(e) => {
                    setPrice(e.target.value);
                    if (errors.price) setErrors((p) => ({ ...p, price: "" }));
                  }}
                  placeholder="45000"
                  min="0"
                />
              </div>
              {errors.price && (
                <span className="ap-error-text">{errors.price}</span>
              )}
            </div>
          </div>

          {/* ════ CATEGORY ════ */}
          <div className="ap-section">
            <p className="ap-section-label">Category</p>
            <div className="ap-field">
              <label className="ap-label" htmlFor="edit-prod-category">
                Category *
              </label>
              <div className="ap-input-wrap">
                <span className="ap-input-icon">
                  <LayoutGrid size={15} />
                </span>
                <select
                  className={`ap-select ${errors.category ? "ap-input--error" : ""}`}
                  id="edit-prod-category"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    if (errors.category)
                      setErrors((p) => ({ ...p, category: "" }));
                  }}
                  style={{ paddingLeft: "2.55rem" }}
                >
                  <option value="">Select category</option>
                  {allCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              {errors.category && (
                <span className="ap-error-text">{errors.category}</span>
              )}
            </div>
          </div>

          {/* ════ Product Materials ════ */}
          <div className="ap-section">
            <p className="ap-section-label">Product Materials</p>
            <p className="ap-pm-hint">
              Update which raw materials are consumed to manufacture one unit of
              this product.
            </p>

            {/* ── Product Materials Input Row ── */}
            <div className="ap-pm-input-card">
              <div className="ap-pm-input-grid">
                {/* Category */}
                <div className="ap-pm-input-field">
                  <label className="ap-pm-field-label">Category</label>
                  <select
                    className="ap-pm-input"
                    value={pmCategory}
                    onChange={(e) => {
                      setPmCategory(e.target.value);
                    }}
                  >
                    <option value="">Select Category…</option>
                    {allCategoriesForMaterial.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Material dropdown */}
                <div className="ap-pm-input-field">
                  <label className="ap-pm-field-label">Material</label>
                  <select
                    className={`ap-pm-select ${pmErrors.material ? "ap-input--error" : ""}`}
                    value={material}
                    onChange={(e) => {
                      const selected = allMaterial.find(
                        (item) => String(item.id) === e.target.value,
                      );
                      setMaterial(e.target.value);
                      setUnit(selected?.unit || "");
                      if (pmErrors.material)
                        setPmErrors((p) => ({ ...p, material: "" }));
                    }}
                  >
                    <option value="">Select material…</option>
                    {allMaterial?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  {pmErrors.material && (
                    <span className="ap-error-text">{pmErrors.material}</span>
                  )}
                </div>

                {/* Quantity */}
                <div className="ap-pm-input-field">
                  <label className="ap-pm-field-label">Qty per Unit</label>
                  <input
                    className={`ap-pm-input ${pmErrors.quantity ? "ap-input--error" : ""}`}
                    type="number"
                    min="0"
                    step="1"
                    placeholder="0"
                    value={quantity}
                    onChange={(e) => {
                      setQuantity(e.target.value);
                      if (pmErrors.quantity)
                        setPmErrors((p) => ({ ...p, quantity: "" }));
                    }}
                  />
                  {pmErrors.quantity && (
                    <span className="ap-error-text">{pmErrors.quantity}</span>
                  )}
                </div>

                {/* Unit — auto fill, read only */}
                <div className="ap-pm-input-field">
                  <label className="ap-pm-field-label">Unit</label>
                  <input
                    className="ap-pm-input ap-pm-input--readonly"
                    value={unit}
                    readOnly
                    placeholder="—"
                    tabIndex={-1}
                  />
                </div>

                {/* Attach button */}
                <div className="ap-pm-input-field ap-pm-attach-wrap">
                  <label className="ap-pm-field-label" aria-hidden="true">
                    &nbsp;
                  </label>
                  <button
                    type="button"
                    className="ap-pm-attach-btn"
                    onClick={handleAttachMaterial}
                    title="Attach material to list"
                    aria-label="Attach material"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* ── Attached Materials Table ── */}
            {attachedMaterials?.length > 0 && (
              <div className="ap-pm-table-wrap">
                <Table className="ap-pm-table" responsive>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Category</th>
                      <th className="text-center">Quantity</th>
                      <th className="text-center">Unit</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attachedMaterials.map((row) => (
                      <tr key={row.id}>
                        <td className="ap-pm-td-name">
                          {row.admaterial?.name}
                        </td>
                        <td>
                          <span className="ap-pm-badge">
                            {row.admaterial?.adcategory?.name}
                          </span>
                        </td>
                        <td className="text-center">{row.qty}</td>
                        <td className="text-center">
                          <span className="ap-pm-unit">
                            {row.admaterial?.unit || "—"}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="ap-pm-delete-btn"
                            onClick={() => handleDeleteMaterial(row.id)}
                            aria-label={`Remove ${row.admaterial?.name}`}
                          >
                            <X size={13} />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          {/* ── Form Actions ── */}
          <div className="ap-actions">
            <button
              type="button"
              className="ap-cancel-btn"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button type="submit" className="ap-save-btn" disabled={isSaving}>
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default EditProduct;
